import { Injectable, ElementRef, Renderer2 } from '@angular/core';
import { AbstractControl } from '@angular/forms';

export interface ValidationState {
  isValid: boolean;
  isInvalid: boolean;
  isTouched: boolean;
  isEmpty: boolean;
  isRequired: boolean;
  hasMinValue: boolean;
  errorKey?: string;
  errorValue?: any;
}

@Injectable({
  providedIn: 'root',
})
export class ValidationService {
  private readonly ERROR_BORDER_COLOR = '#dc3545';
  private readonly SUCCESS_BORDER_COLOR = '#28a745';
  private readonly ERROR_SHADOW = '0 0 0 0.2rem rgba(220, 53, 69, 0.25)';
  private readonly SUCCESS_SHADOW = '0 0 0 0.2rem rgba(40, 167, 69, 0.25)';

  constructor() {}

  getValidationState(
    control: AbstractControl | null,
    elementRef: ElementRef
  ): ValidationState {
    if (!control) {
      return this.getEmptyValidationState();
    }

    const isRequired = elementRef.nativeElement.hasAttribute('required');
    const minValue = elementRef.nativeElement.getAttribute('min');
    const isEmpty =
      !control.value || control.value === '' || control.value <= 0;
    const isTouched = control.dirty || control.touched;
    const hasMinValue = minValue !== null;

    const isInvalid =
      control.invalid ||
      (isRequired && isEmpty) ||
      (hasMinValue && control.value && control.value < parseFloat(minValue));

    const isValid = !isInvalid && control.valid;

    let errorKey: string | undefined;
    let errorValue: any;

    if (control.errors) {
      errorKey = Object.keys(control.errors)[0];
      errorValue = control.errors[errorKey];
    } else if (isRequired && isEmpty) {
      errorKey = 'required';
      errorValue = null;
    } else if (
      hasMinValue &&
      control.value &&
      control.value < parseFloat(minValue)
    ) {
      errorKey = 'min';
      errorValue = { min: minValue };
    }

    return {
      isValid,
      isInvalid,
      isTouched,
      isEmpty,
      isRequired,
      hasMinValue,
      errorKey,
      errorValue,
    };
  }

  shouldShowError(validationState: ValidationState): boolean {
    return validationState.isInvalid && validationState.isTouched;
  }

  shouldApplySuccessBorder(validationState: ValidationState): boolean {
    return validationState.isValid && !validationState.isEmpty;
  }

  private getEmptyValidationState(): ValidationState {
    return {
      isValid: false,
      isInvalid: false,
      isTouched: false,
      isEmpty: true,
      isRequired: false,
      hasMinValue: false,
    };
  }

  applyErrorBorder(elementRef: ElementRef, renderer: Renderer2): void {
    renderer.setStyle(
      elementRef.nativeElement,
      'border',
      `1px solid ${this.ERROR_BORDER_COLOR}`
    );
    renderer.setStyle(
      elementRef.nativeElement,
      'box-shadow',
      this.ERROR_SHADOW
    );
  }

  applySuccessBorder(elementRef: ElementRef, renderer: Renderer2): void {
    renderer.setStyle(
      elementRef.nativeElement,
      'border',
      `1px solid ${this.SUCCESS_BORDER_COLOR}`
    );
    renderer.setStyle(
      elementRef.nativeElement,
      'box-shadow',
      this.SUCCESS_SHADOW
    );
  }

  removeBorderStyles(elementRef: ElementRef, renderer: Renderer2): void {
    renderer.removeStyle(elementRef.nativeElement, 'border');
    renderer.removeStyle(elementRef.nativeElement, 'box-shadow');
  }
}
